import { useCallback } from 'react'
import useCart from './use-cart'
import {
  cartLineItemRemoveMutation,
  getCartId,
  normalizeShopifyCart,
} from '../utils'
import fetcherFn from '../utils/fetcher'
import type { HookFetcherFn } from '../utils/types'
import type { MaybeCart, LineItem, RemoveItemHook } from '../types/cart'

export type RemoveItemActionInput<T = any> = T extends LineItem
  ? Partial<RemoveItemHook['actionInput']>
  : RemoveItemHook['actionInput']

export type RemoveItemFn<T = any> = T extends LineItem
  ? (input?: RemoveItemActionInput<T>) => Promise<MaybeCart>
  : (input: RemoveItemActionInput<T>) => Promise<MaybeCart>

export type UseRemoveItem = <T extends LineItem | undefined = undefined>(ctx?: {
  item?: T
}) => (input: Partial<{ id: string }>) => Promise<MaybeCart>

export type RemoveItemFetcher = HookFetcherFn<RemoveItemHook>

const useRemoveItem: UseRemoveItem = (ctx = {}) => {
  const { item } = ctx
  const { mutate } = useCart()

  const hookFetcher: RemoveItemFetcher = async (hookFetcherContext) => {
    const {
      options,
      fetch,
      input: { itemId },
    } = hookFetcherContext
    const cartId = getCartId()
    const lineItemIds = [itemId]
    const variables = { cartId, lineItemIds }
    const { cartLinesRemove } = await fetch({ ...options, variables })

    return normalizeShopifyCart(cartLinesRemove.cart)
  }

  const removeItem: RemoveItemFn<LineItem> = async (input) => {
    const itemId = input?.id ?? item?.id

    if (!itemId) {
      throw new Error('Invalid input used for this operation')
    }

    const hookFetcherContext = {
      input: { itemId },
      fetch: fetcherFn,
      options: { query: cartLineItemRemoveMutation },
    }
    const data = await hookFetcher(hookFetcherContext)
    await mutate(data, false)
    return data
  }

  return useCallback(removeItem, [mutate, item?.id])
}

export default useRemoveItem
